import React, { useState, useEffect, useMemo } from 'react';
import { UserPlus, Edit2, Trash2, Shield, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ROLE_OPTIONS = ['admin', 'staff', 'gate'];

export default function SuperAdminPanel() {
  const [users, setUsers] = useState([]);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ name: '', mobile: '', staff_id: '' });
  const [savingRoles, setSavingRoles] = useState(null);
  // Filters
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // hide soft-deleted by default
  const [extraRoleFilter, setExtraRoleFilter] = useState('all'); // 'all' | 'admin' | 'staff' | 'gate'

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter !== 'all' && (u.status || 'active') !== statusFilter) return false;
      if (extraRoleFilter !== 'all' && !(u.additional_roles || []).includes(extraRoleFilter)) return false;
      if (q && !(
        (u.name || '').toLowerCase().includes(q) ||
        (u.mobile || '').toLowerCase().includes(q) ||
        (u.staff_id || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter, extraRoleFilter]);

  const adminCount = useMemo(() => users.filter(u => u.role === 'admin' && (u.status || 'active') !== 'deleted').length, [users]);
  const hasAnyFilter = search || roleFilter !== 'all' || statusFilter !== 'active' || extraRoleFilter !== 'all';
  const clearFilters = () => {
    setSearch(''); setRoleFilter('all'); setStatusFilter('active'); setExtraRoleFilter('all');
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/staff`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setUsers(data);
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/super-admin/add-admin?name=${newAdmin.name}&mobile=${newAdmin.mobile}&staff_id=${newAdmin.staff_id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      const data = await response.json();
      alert(data.message);
      setShowAddAdmin(false);
      setNewAdmin({ name: '', mobile: '', staff_id: '' });
      fetchUsers();
    } catch (err) {
      alert('Failed to add admin');
    }
  };

  const handleEditUser = async (userId, updates) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams(updates).toString();
      await fetch(`${API_URL}/api/super-admin/edit-user/${userId}?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      alert('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/super-admin/delete-user/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      alert('Failed to delete user');
    }
  };

  // Toggle one of the *additional* roles for a user. Primary `role` is left
  // unchanged — this just lets the same mobile log in as a different tab.
  const toggleAdditionalRole = async (user, roleToToggle) => {
    if (roleToToggle === user.role) return; // primary role can't be toggled here
    const current = Array.isArray(user.additional_roles) ? user.additional_roles : [];
    const next = current.includes(roleToToggle)
      ? current.filter(r => r !== roleToToggle)
      : [...current, roleToToggle];

    setSavingRoles(user.id);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/super-admin/user/${user.id}/roles`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ additional_roles: next })
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || 'Failed to update roles');
        return;
      }
      fetchUsers();
    } catch {
      alert('Network error while updating roles');
    } finally {
      setSavingRoles(null);
    }
  };

  return (
    <div>
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 mb-6 text-white">
        <div className="flex items-center space-x-3">
          <Shield size={32} />
          <div>
            <h2 className="text-2xl font-bold">Super Admin Panel</h2>
            <p className="text-purple-100 text-sm">Full system control</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setShowAddAdmin(!showAddAdmin)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="add-admin-btn"
          >
            <UserPlus size={20} />
            <span>Add Admin</span>
          </button>
          <div className="text-xs text-gray-600">
            <strong>{adminCount}</strong> admin{adminCount === 1 ? '' : 's'} · showing <strong>{filteredUsers.length}</strong> of {users.length} users
          </div>
        </div>

        {/* Filter bar — search by name/mobile/ID + role + extra-role + status */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-2" data-testid="superadmin-filter-bar">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, mobile or staff ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-200"
              data-testid="superadmin-search"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            data-testid="superadmin-role-filter"
            title="Primary role"
          >
            <option value="all">All primary roles</option>
            <option value="admin">Admin</option>
            <option value="staff">Staff</option>
            <option value="gate">Gate</option>
          </select>
          <select
            value={extraRoleFilter}
            onChange={(e) => setExtraRoleFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            data-testid="superadmin-extra-role-filter"
            title="Has additional login role"
          >
            <option value="all">Any extra role</option>
            <option value="admin">Also Admin</option>
            <option value="staff">Also Staff</option>
            <option value="gate">Also Gate</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
            data-testid="superadmin-status-filter"
          >
            <option value="active">Active only</option>
            <option value="all">All statuses</option>
            <option value="deleted">Deleted only</option>
          </select>
          {hasAnyFilter && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
              data-testid="superadmin-clear-filters"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {showAddAdmin && (
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-purple-200 mb-6">
          <h3 className="font-semibold mb-4 text-purple-900">Add New Admin</h3>
          <form onSubmit={handleAddAdmin} className="space-y-4">
            <input
              type="text"
              placeholder="Admin Name"
              value={newAdmin.name}
              onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              required
            />
            <input
              type="tel"
              placeholder="Mobile (10 digits)"
              value={newAdmin.mobile}
              onChange={(e) => setNewAdmin({...newAdmin, mobile: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              required
            />
            <input
              type="text"
              placeholder="Staff ID"
              value={newAdmin.staff_id}
              onChange={(e) => setNewAdmin({...newAdmin, staff_id: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              required
            />
            <div className="bg-purple-50 p-3 rounded-lg text-sm text-purple-800">
              💡 Password = Mobile number
            </div>
            <div className="flex space-x-3">
              <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                Create Admin
              </button>
              <button type="button" onClick={() => setShowAddAdmin(false)} className="px-4 py-2 bg-gray-200 rounded-lg">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Primary Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Also-login as
                <span className="block text-[9px] font-normal text-gray-400 normal-case">Same mobile, different login tab</span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500" data-testid="superadmin-empty">
                  {users.length === 0 ? 'Loading users…' : 'No users match the current filters.'}
                </td>
              </tr>
            )}
            {filteredUsers.map(user => (
              <tr key={user.id} className={user.status === 'deleted' ? 'opacity-50' : ''}>
                <td className="px-4 py-3">
                  {editingUser === user.id ? (
                    <input
                      type="text"
                      defaultValue={user.name}
                      className="border rounded px-2 py-1"
                      onBlur={(e) => handleEditUser(user.id, { name: e.target.value })}
                    />
                  ) : (
                    <span className="font-medium">{user.name}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{user.mobile}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid={`extra-roles-${user.id}`}>
                    {ROLE_OPTIONS.filter(r => r !== user.role).map(r => {
                      const enabled = (user.additional_roles || []).includes(r);
                      const isSaving = savingRoles === user.id;
                      return (
                        <button
                          key={r}
                          type="button"
                          disabled={isSaving || user.status === 'deleted'}
                          onClick={() => toggleAdditionalRole(user, r)}
                          className={`px-2 py-0.5 text-[11px] font-medium rounded-full border transition-colors ${
                            enabled
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                              : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                          } ${isSaving ? 'opacity-60' : ''}`}
                          data-testid={`extra-role-${user.id}-${r}`}
                          title={enabled ? `Disable ${r} login` : `Enable ${r} login for this mobile`}
                        >
                          {enabled ? '✓ ' : '+ '}{r.charAt(0).toUpperCase() + r.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {user.mobile !== '+919894021299' && (
                    <>
                      <button
                        onClick={() => setEditingUser(user.id)}
                        className="text-blue-600 hover:text-blue-700 p-1"
                        title="Edit"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-600 hover:text-red-700 p-1"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
